import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import clsx from 'clsx';

import { useDevice } from '@bonnet/next/device';

import { useBrand } from '@atc/brand';
import { imageSizes } from '@atc/images';

import {
    Button,
    PositionedOverlay,
    SubHeading,
    Text,
} from 'reaxl';
import { clickTypes, sendClick, sendImpressions } from 'reaxl-analytics';
import { inventoryImpressions } from 'reaxl-analytics-handlers';
import { EmailCTA } from 'reaxl-email';
import { InventoryListingSaveGlyph } from 'reaxl-listing';
import {
    HotCar,
    InventoryImage,
    InventoryImagePlaceholder,
    NewlyListed,
    OwnerPhoneNumber,
    PricingV2,
    ReducedPrice,
} from 'reaxl-molecules';

import { saveInvListing } from '@/utilities/saveListing';

import { inventoryClick } from '@/analytics/inventoryAnalyticsHandlers';

import invActiveEmailListingDuck from '@/ducks/inv/invActiveEmailListingDuck';
import invActiveInteractionDuck from '@/ducks/inv/invActiveInteractionDuck';
import invResultsDuck from '@/ducks/inv/invResultsDuck';
import invSimilarListingsDuck from '@/ducks/inv/invSimilarListingsDuck';
import ownersDuck from '@/ducks/ownersDuck.js';
import queryDuck from '@/ducks/queryDuck.js';
import savedInventoryDuck from '@/ducks/savedInventoryDuck.js';

import useVdpNavigation from '@/hooks/useVdpNavigation';

function InvDetailsContainer() {

    const dispatch = useDispatch();

    const device = useDevice();
    const isXs = device.is.xs;
    const isSm = device.is.sm;
    const isMd = device.is.md;

    const navigateToVdp = useVdpNavigation();

    const { brand } = useBrand();

    const listing = useSelector(invResultsDuck.selectors.getInvActiveInventory);
    const owner = useSelector(ownersDuck.selectors.getOwners)[listing.ownerId];
    const similarListings = useSelector(invSimilarListingsDuck.selectors.getActiveInventory);
    const similarListingIds = useMemo(() => similarListings.map((similarListing) => similarListing.id), [similarListings]);

    useEffect(() => {
        dispatch(invSimilarListingsDuck.creators.getSimilarListings({ listing: { ...listing, owner } }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const query = useSelector(queryDuck.selectors.getDuckState);
    const {
        id,
        daysOnSite,
        images,
        mileage,
        ownerId,
        pricingDetail,
        title,
        phone,
        isHot,
        isNewlyListed,
        isReducedPrice,
    } = listing;

    const showVehicleBadges = isHot || isNewlyListed || isReducedPrice;

    const primaryImage = images?.sources?.[images.primary]?.src;

    const { inventoryListing, inventoryCard, inventoryThumbxLg } = imageSizes;

    const savedListingIds = useSelector(savedInventoryDuck.selectors.getSavedInventoryIds);
    const emailCTAContext = (brand === 'kbb') ? 'inventory' : 'confirmAvailability';

    const hasSentImpression = useRef(false);

    useEffect(() => {
        if (!hasSentImpression.current && similarListingIds.length > 0) {
            sendImpressions({
                name: inventoryImpressions,
                data: {
                    selectedItemIds: similarListingIds,
                    pixallData: {
                        label: 'hybrid_v_lstg',
                        impressionType: 'hybrid listing',
                        listingType: 'hybrid listing',
                    },
                },
            });

            // preventing sending duplicated impression event
            hasSentImpression.current = true;
        }
    }, [similarListingIds]);

    const onSaveToggle = useCallback((event, savedListing, wasSaved) => {
        dispatch(saveInvListing({
            event,
            listing: savedListing,
            wasSaved,
            sendClick,
        }));
    }, [dispatch]);

    const handleEmailOwnerClick = useCallback((context) => {
        // TODO: Add Analytics

        dispatch(invActiveInteractionDuck.creators.setKeys({
            // clickType: '',
            emailCTAContext,
            parentId: 'hybrid listing',
            showEmailModal: true,
        }));

        dispatch(invActiveEmailListingDuck.creators.setActiveResults([id]));
    }, [dispatch, emailCTAContext, id]);

    const handleViewListingDetailsClick = useCallback(async (event, par = 'hybrid_v_lstg') => {
        sendClick(inventoryClick, event, {
            inventoryId: id,
            par,
            pixallData: {
                eventSource: 'vehicle',
                listingType: 'hybrid listing',
            },
        });

        dispatch(invSimilarListingsDuck.creators.clear());

        navigateToVdp({
            ...query,
            clickType: clickTypes.HYBRID,
        });
    }, [dispatch, id, navigateToVdp, query]);

    const analyticsData = {
        par: 'call_seller',
        inventoryId: id,
        ownerId: listing.ownerId,
        listingType: 'hybrid listing',
    };
    const getButtonSize = () => {
        if (isXs || isSm) return 'small';
        if (isMd) return 'medium';
        return 'large';
    };

    const buttonSize = getButtonSize();

    const renderListingDetailsCTA = (
        <Button
            className={clsx({ 'margin-top-3': isXs })}
            onClick={(e) => handleViewListingDetailsClick(e, null)}
            bsSize={buttonSize}
            block={isXs}
        >
            View Details
        </Button>
    );

    const renderUrgencyDriver = () => {
        if (isHot) return <HotCar description="Highly likely to sell within a week" />;
        if (isNewlyListed && (daysOnSite > 0 && daysOnSite < 30)) return <NewlyListed />;
        if (isReducedPrice) return <ReducedPrice label="Recent Price Drop" />;
        return null;
    };

    const imageSize = () => {
        if (isXs) return inventoryThumbxLg;
        if (isSm) return inventoryCard;
        return inventoryListing;
    };

    return (
        <div
            className="margin-vertical-5 padding-horizontal-md-5"
            data-cmp="invDetailsContainer"
        >
            <div
                className="display-flex gap-3"
            >
                <div className="display-flex flex-shrink-0">
                    <PositionedOverlay shrinkwrap={false}>
                        <PositionedOverlay.Base>
                            {primaryImage ? (
                                <InventoryImage
                                    lazyLoad={false}
                                    loading="eager"
                                    src={primaryImage}
                                    sizes={imageSize()}
                                    onClick={handleViewListingDetailsClick}
                                    className="cursor-pointer"
                                />
                            ) : (
                                <InventoryImagePlaceholder imgClassNames="img-responsive-scale" />
                            )}
                        </PositionedOverlay.Base>
                        <PositionedOverlay.Anchor
                            position="topRight"
                            offsetBottom={25}
                            offsetLeft={25}
                        >
                            <InventoryListingSaveGlyph
                                uiContext="accent-circle"
                                onSaveToggle={onSaveToggle}
                                listing={listing}
                                isSaved={savedListingIds.includes(listing.id)}
                            />
                        </PositionedOverlay.Anchor>
                    </PositionedOverlay>
                </div>
                <div>
                    <div>
                        {showVehicleBadges && <div className="margin-bottom-2">{renderUrgencyDriver()}</div>}

                        <Button
                            bsStyle="text"
                            onClick={handleViewListingDetailsClick}
                            className="text-gray-base"
                        >
                            <SubHeading level={1}>{title}</SubHeading>
                        </Button>

                        {mileage?.value && (
                            <Text
                                className="margin-vertical-2"
                                color="subdued-lighter"
                                componentClass="div"
                                size={400}
                            >
                                {mileage.value}
                                {' '}
                                miles
                            </Text>
                        )}

                        <PricingV2
                            key="inventory-details-pricing"
                            data={pricingDetail}
                        />

                        <div className={clsx({ 'justify-content-between': isXs }, 'display-flex margin-top-3')}>
                            <EmailCTA
                                buttonSize={buttonSize}
                                className={!isXs ? 'margin-right-3' : 'margin-right-3 w-100'}
                                key="email-cta"
                                listingId={listing.id}
                                onClick={handleEmailOwnerClick}
                                ownerId={ownerId}
                                context={emailCTAContext}
                                parentId="hybrid listing"
                                showIcon={false}
                                labelKey="checkAvailability"
                                type={isXs && 'block'}
                            />
                            {!isXs && renderListingDetailsCTA}
                            {isXs && (
                                <OwnerPhoneNumber
                                    key="ownerPhoneNumber"
                                    analyticsData={analyticsData}
                                    buttonLabel="Call"
                                    buttonSize="small"
                                    buttonStyle="default"
                                    phone={phone}
                                    isVisible
                                    isCallable
                                    uiContext="button"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {isXs && renderListingDetailsCTA}
        </div>
    );
}

export default React.memo(InvDetailsContainer);
